import React, { useState } from 'react';
import { useApiData } from '../../contexts/apiService';

// ─── Types ────────────────────────────────────────────────────────────────────

interface NameCorrection {
    field: string;
    original: string;
    corrected: string;
}

interface PricingStep {
    label: string;
    value: number;
}

interface ProducerPricing {
    steps: PricingStep[];
    purchase_price_per_bbl: number;
    net_barrels: number;
    total_purchase: number;
    /** Per-load charge added to total_purchase after per-bbl calc (e.g. Steel Reef BSW fee). */
    additional_charges_total?: number;
    /** Non-zero when the pipeline uses a producer-specific in-memory flag override
     *  (BCP/Tobe Foster, San Mateo) that the audit cannot fully replicate. */
    step_reconciliation_gap?: number;
    /** True when Tall Oak tiered pricing was applied; purchase_price_per_bbl is a
     *  blended average across volume brackets, not a direct step sum. */
    is_tall_oak?: boolean;
}

interface MarketPricing {
    steps: PricingStep[];
    sale_price_per_bbl: number;
    /** sale_price_per_bbl + station_charge_per_bbl; for reference only. */
    effective_sale_price_per_bbl: number;
    /** Per-bbl station charge rate, read directly from market_discounts record. */
    station_charge_per_bbl?: number;
    net_barrels: number;
    total_sale: number;
    /** Raw total from blob (rate × gross_bbl). */
    station_charge_total?: number;
}

interface Ticket {
    ticket_number: string | number;
    load_date: string;
    producer: string;
    lease: string;
    net_barrels: number;
    gross_barrels: number;
    bsw: number;
    observed_gravity: number;
    corrected_gravity: number;
    carrier: string;
    name_corrections?: NameCorrection[];
    producer_pricing: ProducerPricing;
}

interface Delivery {
    destination: string;
    market: string;
    net_barrels: number;
    gross_barrels: number;
    is_extra_offload: boolean;
    name_corrections?: NameCorrection[];
    market_pricing: MarketPricing;
}

interface CarrierCost {
    mileage: number;
    approved_mileage: number;
    rate: number;
    approved_rate: number;
    effective_rate: number;
    total_gross_barrels: number;
    min_load_applied: boolean;
    billed_barrels: number;
    total_trucking: number;
    trucking_net: number;
    split_applied: boolean;
}

interface RidSummary {
    total_purchase: number;
    total_sale: number;
    total_trucking: number;
    trucking_net: number;
    /** True when a split was applied and trucking_net differs from total_trucking. */
    split_applied: boolean;
    total_station_charge?: number;
    net_profit: number;
    producer: string;
    carrier: string;
    status: string;
}

interface ConsistencyCheck {
    label: string;
    computed: number | null;
    stored: number | null;
    delta: number;
    match: boolean;
}

interface TicketConsistency {
    ticket_number: string | number;
    db_found: boolean;
    checks: ConsistencyCheck[];
}

interface DeliveryConsistency {
    destination: string;
    db_found: boolean;
    checks: ConsistencyCheck[];
}

interface DbConsistency {
    never_processed: boolean;
    any_discrepancy: boolean;
    rid_level: ConsistencyCheck[];
    tickets: TicketConsistency[];
    deliveries: DeliveryConsistency[];
}

interface AuditRow {
    rid: string;
    tickets: Ticket[];
    deliveries: Delivery[];
    carrier_cost: CarrierCost;
    rid_summary: RidSummary;
    db_consistency?: DbConsistency;
}

// ─── Formatting helpers ────────────────────────────────────────────────────────

const fmt$ = (v: number) =>
    v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const fmtNum = (v: number, decimals = 2) =>
    v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

// ─── Sub-components ───────────────────────────────────────────────────────────

const ConsistencyTable: React.FC<{ checks: ConsistencyCheck[] }> = ({ checks }) => (
    <table className="audit-step-table consistency-table">
        <thead>
            <tr>
                <th className="step-label">Field</th>
                <th className="step-value">Computed</th>
                <th className="step-value">Stored</th>
                <th className="step-value">Δ</th>
            </tr>
        </thead>
        <tbody>
            {checks.map((c, i) => (
                <tr key={i} className={c.match ? '' : 'consistency-mismatch'}>
                    <td className="step-label">{c.label}</td>
                    <td className="step-value">{c.computed !== null ? fmt$(c.computed) : '—'}</td>
                    <td className="step-value">{c.stored !== null ? fmt$(c.stored) : '—'}</td>
                    <td className="step-value">{c.match ? '✅' : fmt$(c.delta)}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

const DbConsistencyPanel: React.FC<{ consistency: DbConsistency }> = ({ consistency }) => {
    const [open, setOpen] = useState(false);
    const { never_processed, any_discrepancy, rid_level, tickets, deliveries } = consistency;

    const headerClass = never_processed
        ? 'consistency-header consistency-warn'
        : any_discrepancy
        ? 'consistency-header consistency-fail'
        : 'consistency-header consistency-pass';

    const summary = never_processed
        ? '⚠ Not yet processed — no stored values found in crude_main'
        : any_discrepancy
        ? '✗ Discrepancies found between computed and stored values'
        : '✓ All values match stored database records';

    return (
        <div className="audit-section audit-consistency-section">
            <div className={headerClass} onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer' }}>
                <span>{summary}</span>
                <span className="audit-chevron" style={{ float: 'right' }}>{open ? '▲' : '▼'}</span>
            </div>
            {open && (
                <div style={{ padding: '0.5rem 0.85rem' }}>
                    <h5 className="audit-section-title">RID-Level</h5>
                    <ConsistencyTable checks={rid_level} />

                    {tickets.map((t, i) => (
                        <div key={i}>
                            <h5 className="audit-section-title">
                                Ticket #{t.ticket_number}
                                {!t.db_found && <span className="audit-badge-warning" style={{ marginLeft: 8 }}>Not in DB</span>}
                            </h5>
                            <ConsistencyTable checks={t.checks} />
                        </div>
                    ))}

                    {deliveries.map((d, i) => (
                        <div key={i}>
                            <h5 className="audit-section-title">
                                Delivery: {d.destination}
                                {!d.db_found && <span className="audit-badge-warning" style={{ marginLeft: 8 }}>Not in DB</span>}
                            </h5>
                            <ConsistencyTable checks={d.checks} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const StepTable: React.FC<{ steps: PricingStep[]; finalLabel: string; finalValue: number }> = ({
    steps,
    finalLabel,
    finalValue,
}) => (
    <table className="audit-step-table">
        <tbody>
            {steps.map((s, i) => (
                <tr key={i} className={s.value < 0 ? 'step-negative' : ''}>
                    <td className="step-label">{s.label}</td>
                    <td className="step-value">{fmt$(s.value)}</td>
                </tr>
            ))}
            <tr className="step-final">
                <td className="step-label">{finalLabel}</td>
                <td className="step-value">{fmt$(finalValue)}</td>
            </tr>
        </tbody>
    </table>
);

const NameCorrectionBadges: React.FC<{ corrections: NameCorrection[] }> = ({ corrections }) => {
    if (!corrections || corrections.length === 0) return null;
    return (
        <div className="audit-name-corrections">
            {corrections.map((c, i) => (
                <span key={i} className="audit-correction-badge" title={`Original: ${c.original}`}>
                    {c.field} corrected: <s>{c.original}</s> → {c.corrected}
                </span>
            ))}
        </div>
    );
};

const ReconWarning: React.FC<{ gap: number }> = ({ gap }) => (
    <p className="audit-recon-warning">
        ⚠ Step total diverges from computed purchase price by {fmt$(Math.abs(gap))}/bbl.
        This ticket uses a producer-specific pricing override (e.g. BCP/Tobe Foster or San Mateo
        lease) that mutates component flags at runtime — the step labels above reflect the
        database record, not the runtime override.
    </p>
);

const TicketCard: React.FC<{ ticket: Ticket; index: number }> = ({ ticket, index }) => {
    const [open, setOpen] = useState(true);
    const pp = ticket.producer_pricing;

    return (
        <div className="audit-ticket-card">
            <div className="audit-ticket-header" onClick={() => setOpen(o => !o)}>
                <span className="audit-ticket-title">
                    Ticket #{ticket.ticket_number ?? '—'} &nbsp;·&nbsp; {ticket.load_date}
                </span>
                <span className="audit-ticket-meta">
                    {ticket.producer} / {ticket.lease} &nbsp;·&nbsp; {fmtNum(ticket.net_barrels)} net bbl
                </span>
                <span className="audit-chevron">{open ? '▲' : '▼'}</span>
            </div>
            <NameCorrectionBadges corrections={ticket.name_corrections ?? []} />

            {open && (
                <div className="audit-ticket-body">
                    {/* Load Details */}
                    <div className="audit-section">
                        <h4 className="audit-section-title">Load Details</h4>
                        <div className="audit-detail-grid">
                            <div><span className="detail-key">Producer</span><span className="detail-val">{ticket.producer}</span></div>
                            <div><span className="detail-key">Lease</span><span className="detail-val">{ticket.lease}</span></div>
                            <div><span className="detail-key">Carrier</span><span className="detail-val">{ticket.carrier}</span></div>
                            <div><span className="detail-key">Load Date</span><span className="detail-val">{ticket.load_date}</span></div>
                            <div><span className="detail-key">Gross Bbl</span><span className="detail-val">{fmtNum(ticket.gross_barrels)}</span></div>
                            <div><span className="detail-key">Net Bbl</span><span className="detail-val">{fmtNum(ticket.net_barrels)}</span></div>
                            <div><span className="detail-key">BSW %</span><span className="detail-val">{fmtNum(ticket.bsw, 3)}</span></div>
                            <div><span className="detail-key">Obs. Gravity</span><span className="detail-val">{fmtNum(ticket.observed_gravity, 1)}</span></div>
                            <div><span className="detail-key">Corr. Gravity</span><span className="detail-val">{fmtNum(ticket.corrected_gravity, 1)}</span></div>
                        </div>
                    </div>

                    {/* Producer Pricing */}
                    <div className="audit-section">
                        <h4 className="audit-section-title">Producer Pricing</h4>
                        {pp.is_tall_oak && (
                            <p className="audit-recon-warning">
                                ℹ Tall Oak tiered pricing: purchase price shown is the blended
                                average across volume brackets. Steps reflect the base deduct
                                and components only.
                            </p>
                        )}
                        <StepTable
                            steps={pp.steps}
                            finalLabel={`Purchase Price / bbl`}
                            finalValue={pp.purchase_price_per_bbl}
                        />
                        {pp.step_reconciliation_gap !== undefined &&
                            Math.abs(pp.step_reconciliation_gap) > 0.01 && (
                            <ReconWarning gap={pp.step_reconciliation_gap} />
                        )}
                        <div className="audit-subtotal-row">
                            <span>{fmtNum(pp.net_barrels)} net bbl × {fmt$(pp.purchase_price_per_bbl)}/bbl</span>
                            <span className="subtotal-value">= {fmt$(pp.net_barrels * pp.purchase_price_per_bbl)}</span>
                        </div>
                        {pp.additional_charges_total !== undefined &&
                            pp.additional_charges_total !== 0 && (
                            <div className="audit-subtotal-row">
                                <span>Processing fee (per load)</span>
                                <span className={`subtotal-value${pp.additional_charges_total < 0 ? ' step-negative' : ''}`}>
                                    {fmt$(pp.additional_charges_total)}
                                </span>
                            </div>
                        )}
                        <div className="audit-subtotal-row audit-subtotal-total">
                            <span>Total Purchase</span>
                            <span className="subtotal-value">{fmt$(pp.total_purchase)}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const DeliveryCard: React.FC<{ delivery: Delivery; index: number }> = ({ delivery, index }) => {
    const mp = delivery.market_pricing;

    return (
        <div className="audit-delivery-card">
            <div className="audit-delivery-header">
                <span className="audit-ticket-title">
                    {delivery.is_extra_offload ? 'Extra Offload: ' : ''}{delivery.destination}
                </span>
                <span className="audit-ticket-meta">
                    {delivery.market} &nbsp;·&nbsp; {fmtNum(delivery.net_barrels)} net bbl
                </span>
            </div>
            <NameCorrectionBadges corrections={delivery.name_corrections ?? []} />
            <div className="audit-ticket-body">
                <div className="audit-section">
                    <h4 className="audit-section-title">Market Pricing</h4>
                    <StepTable
                        steps={mp.steps}
                        finalLabel="Sale Price / bbl"
                        finalValue={mp.sale_price_per_bbl}
                    />
                    <div className="audit-subtotal-row">
                        <span>{fmtNum(mp.net_barrels)} net bbl × {fmt$(mp.sale_price_per_bbl)}/bbl</span>
                        <span className="subtotal-value">= {fmt$(mp.total_sale)}</span>
                    </div>
                    {(mp.station_charge_per_bbl ?? 0) !== 0 && (
                        <div className="audit-subtotal-row">
                            <span>
                                Station Charge &nbsp;·&nbsp;
                                {fmtNum(mp.net_barrels)} net bbl × {fmt$(mp.station_charge_per_bbl!)}/bbl
                            </span>
                            <span className="subtotal-value">{fmt$(mp.station_charge_total ?? 0)}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const CarrierSection: React.FC<{ cc: CarrierCost }> = ({ cc }) => (
    <div className="audit-section audit-carrier-section">
        <h4 className="audit-section-title">Carrier Cost</h4>
        <div className="audit-detail-grid">
            <div><span className="detail-key">Scheduled Mileage</span><span className="detail-val">{cc.mileage || '—'}</span></div>
            <div><span className="detail-key">Approved Mileage</span><span className="detail-val">{cc.approved_mileage || '—'}</span></div>
            <div><span className="detail-key">Rate</span><span className="detail-val">{cc.rate ? fmt$(cc.rate) : '—'}</span></div>
            <div><span className="detail-key">Approved Rate</span><span className="detail-val">{cc.approved_rate ? fmt$(cc.approved_rate) : '—'}</span></div>
            <div><span className="detail-key">Effective Rate</span><span className="detail-val">{fmt$(cc.effective_rate)}</span></div>
            <div><span className="detail-key">Gross Bbl</span><span className="detail-val">{fmtNum(cc.total_gross_barrels)}</span></div>
            {cc.min_load_applied && (
                <div className="detail-full-row">
                    <span className="audit-badge-warning">170 bbl minimum applied — billed on {fmtNum(cc.billed_barrels)} bbl</span>
                </div>
            )}
        </div>
        <table className="audit-step-table" style={{ marginTop: 8 }}>
            <tbody>
                <tr>
                    <td className="step-label">Total Trucking ({fmtNum(cc.billed_barrels)} bbl × {fmt$(cc.effective_rate)})</td>
                    <td className="step-value">{fmt$(cc.total_trucking)}</td>
                </tr>
                {cc.split_applied && (
                    <tr className="step-negative">
                        <td className="step-label">Trucking Net (after split)</td>
                        <td className="step-value">{fmt$(cc.trucking_net)}</td>
                    </tr>
                )}
            </tbody>
        </table>
    </div>
);

const SummaryBar: React.FC<{ summary: RidSummary }> = ({ summary }) => {
    const margin = summary.net_profit;
    return (
        <div className={`audit-summary-bar ${margin < 0 ? 'summary-negative' : 'summary-positive'}`}>
            <div className="summary-item">
                <span className="summary-label">Producer Cost</span>
                <span className="summary-value">{fmt$(summary.total_purchase)}</span>
            </div>
            <div className="summary-item">
                <span className="summary-label">Market Revenue</span>
                <span className="summary-value">{fmt$(summary.total_sale)}</span>
            </div>
            {(summary.total_station_charge ?? 0) !== 0 && (
                <div className="summary-item">
                    <span className="summary-label">Station Charges</span>
                    <span className="summary-value">{fmt$(summary.total_station_charge ?? 0)}</span>
                </div>
            )}
            <div className="summary-item">
                <span className="summary-label">
                    {summary.trucking_net > 0 ? 'Trucking Net' : 'Total Trucking'}
                </span>
                <span className="summary-value">
                    {fmt$(summary.trucking_net > 0 ? summary.trucking_net : summary.total_trucking)}
                </span>
            </div>
            <div className="summary-item summary-profit">
                <span className="summary-label">Net Profit</span>
                <span className="summary-value">{fmt$(margin)}</span>
            </div>
        </div>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const PricingAudit: React.FC = () => {
    const { fetchRidAudit } = useApiData();
    const [ridInput, setRidInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<AuditRow[] | null>(null);

    const handleAudit = async () => {
        const rid = ridInput.trim();
        if (!rid) return;
        setLoading(true);
        setError(null);
        setResults(null);
        try {
            const data = await fetchRidAudit(rid);
            if (data && data.error) {
                setError(data.error);
            } else if (!Array.isArray(data) || data.length === 0) {
                setError('No data found for this RID.');
            } else {
                setResults(data);
            }
        } catch (e: any) {
            setError(e?.message ?? 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleAudit();
    };

    return (
        <div className="pricing-audit-container">
            {/* Search bar */}
            <div className="audit-search-bar">
                <input
                    className="audit-rid-input"
                    type="text"
                    placeholder="Enter RID…"
                    value={ridInput}
                    onChange={e => setRidInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                <button
                    className="audit-run-btn"
                    onClick={handleAudit}
                    disabled={loading || !ridInput.trim()}
                >
                    {loading ? 'Running…' : 'Audit'}
                </button>
            </div>

            {/* States */}
            {loading && <p className="audit-state-msg">Running pricing pipeline…</p>}
            {error && <p className="audit-state-msg audit-state-error">{error}</p>}

            {/* Results */}
            {results && results.map((auditRow) => (
                <div key={auditRow.rid} className="audit-rid-block">
                    <div className="audit-rid-header">
                        <h3>RID {auditRow.rid}</h3>
                        <span className="audit-rid-meta">
                            {auditRow.rid_summary.producer} &nbsp;·&nbsp; {auditRow.rid_summary.carrier} &nbsp;·&nbsp; {auditRow.rid_summary.status}
                        </span>
                    </div>

                    {/* Pickup tickets */}
                    <h4 className="audit-group-label">Pickups ({auditRow.tickets.length})</h4>
                    {auditRow.tickets.map((ticket, i) => (
                        <TicketCard key={`${ticket.ticket_number}-${i}`} ticket={ticket} index={i} />
                    ))}

                    {/* Deliveries */}
                    {auditRow.deliveries.length > 0 && (
                        <>
                            <h4 className="audit-group-label">Deliveries ({auditRow.deliveries.length})</h4>
                            {auditRow.deliveries.map((d, i) => (
                                <DeliveryCard key={i} delivery={d} index={i} />
                            ))}
                        </>
                    )}

                    {/* Carrier Cost */}
                    <CarrierSection cc={auditRow.carrier_cost} />

                    {/* RID Summary */}
                    <SummaryBar summary={auditRow.rid_summary} />

                    {/* DB Consistency Check */}
                    {auditRow.db_consistency && (
                        <DbConsistencyPanel consistency={auditRow.db_consistency} />
                    )}
                </div>
            ))}
        </div>
    );
};

export default PricingAudit;
